import { enumCompanyBusinessType } from '@data-layer/enums/companies/enumCompanyBusinessType';
import { enumCompanyPartnershipType } from '@data-layer/enums/companies/enumCompanyPartnershipType';
import { USA_COUNTRY_CODE } from '@constants/constants';
import { CountryModel } from '@data-layer/models/common';

export const BUSINESS_TYPES: Array<{ value: enumCompanyBusinessType; description: string }> = [
  { value: enumCompanyBusinessType.LLC, description: 'LLC' },
  { value: enumCompanyBusinessType.PARTNERSHIP, description: 'Partnership' },
  { value: enumCompanyBusinessType.SOLE_PROPRIETOR, description: 'SoleProprietor' },
  { value: enumCompanyBusinessType.CORPORATION, description: 'Corporation' },
  { value: enumCompanyBusinessType.INDIVIDUAL, description: 'Individual' },
];

// #13188 — Individual and Sole Proprietor must upload a Photo ID (ID_DOCUMENT).
export const requiresPhotoId = (type: enumCompanyBusinessType): boolean =>
  type === enumCompanyBusinessType.INDIVIDUAL || type === enumCompanyBusinessType.SOLE_PROPRIETOR;

// #13188 — Corporation, LLC and Partnership (US only) must choose a State of Incorporation.
export const requiresStateOfIncorporation = (type: enumCompanyBusinessType): boolean =>
  type === enumCompanyBusinessType.CORPORATION || type === enumCompanyBusinessType.LLC || type === enumCompanyBusinessType.PARTNERSHIP;

// #13520 — the State of Incorporation applies only to a US business (flag) whose ADDRESS is in the US:
// with Business ID "Other" (ANI) the country is editable and a foreign address hides the field.
// A missing country counts as non-US (parity with the ANI application page, #13233).
export const isStateOfIncorporationApplicable = (
  isUnitedStates: boolean,
  country: CountryModel | null | undefined,
  businessType: enumCompanyBusinessType,
): boolean => !!isUnitedStates && !!country && country.code === USA_COUNTRY_CODE && requiresStateOfIncorporation(businessType);

// #13269 — Corporation, LLC and Partnership must provide controlling persons (beneficial owners / partners).
export const requiresControllingPersons = (type: enumCompanyBusinessType): boolean =>
  type === enumCompanyBusinessType.CORPORATION || type === enumCompanyBusinessType.LLC || type === enumCompanyBusinessType.PARTNERSHIP;

export const isPartnership = (type: enumCompanyBusinessType): boolean => type === enumCompanyBusinessType.PARTNERSHIP;

// #13269 — required minimum of controlling persons per business type.
export const minControllingPersons = (type: enumCompanyBusinessType): number => {
  if (isPartnership(type)) {
    return 2;
  }
  return requiresControllingPersons(type) ? 1 : 0;
};

export const PARTNERSHIP_TYPES: Array<{ value: enumCompanyPartnershipType; description: string }> = [
  { value: enumCompanyPartnershipType.GENERAL_PARTNERSHIP, description: 'GP' },
  { value: enumCompanyPartnershipType.LIMITED_PARTNERSHIP, description: 'LP' },
  { value: enumCompanyPartnershipType.LIMITED_LIABILITY_PARTNERSHIP, description: 'LLP' },
];

// #13269 — section heading per variant. LP is deliberately "GENERAL PARTNER" (the design's "GENARAL" is a typo).
export const controllingPersonHeadingKey = (businessType: enumCompanyBusinessType, partnershipType: enumCompanyPartnershipType): string => {
  if (!isPartnership(businessType)) {
    return 'COMPANY.CONTROLLING_PERSONS.BENEFICIAL_OWNER_HEADING';
  }
  return partnershipType === enumCompanyPartnershipType.LIMITED_PARTNERSHIP
    ? 'COMPANY.CONTROLLING_PERSONS.GENERAL_PARTNER_HEADING'
    : 'COMPANY.CONTROLLING_PERSONS.AUTHORIZED_PARTNER_HEADING';
};

// #13269 — role label for missing-fields entries, same branching as the heading.
export const controllingPersonRoleKey = (businessType: enumCompanyBusinessType, partnershipType: enumCompanyPartnershipType): string => {
  if (!isPartnership(businessType)) {
    return 'COMPANY.CONTROLLING_PERSONS.ROLE_BENEFICIAL_OWNER';
  }
  return partnershipType === enumCompanyPartnershipType.LIMITED_PARTNERSHIP
    ? 'COMPANY.CONTROLLING_PERSONS.ROLE_GENERAL_PARTNER'
    : 'COMPANY.CONTROLLING_PERSONS.ROLE_AUTHORIZED_PARTNER';
};

// #13269 — header tooltip only for partnership variants; Corporation/LLC have none (info panel only).
export const controllingPersonTooltipKey = (
  businessType: enumCompanyBusinessType,
  partnershipType: enumCompanyPartnershipType,
): string | null => {
  if (!isPartnership(businessType)) {
    return null;
  }
  return partnershipType === enumCompanyPartnershipType.LIMITED_PARTNERSHIP
    ? 'COMPANY.CONTROLLING_PERSONS.TOOLTIP_LP'
    : 'COMPANY.CONTROLLING_PERSONS.TOOLTIP_GP_LLP';
};
